import { useState, useEffect } from 'react';
import { Card, Typography, Form, Input, Button, Space, Tag, Popconfirm, Spin, message } from 'antd';
import { useInviteEmailTemplate } from '../../hooks/use_invite_email_template';

const { Title, Text } = Typography;

/**
 * Superadmin-only: this node's default wording for the person-invite email.
 * Pre-fills the compose modal behind "Send email invite" on an unclaimed
 * person (Entities/InviteEmailModal.jsx), where it can still be adjusted per
 * recipient.
 *
 * Until it's saved here the node runs on the built-in default from
 * services/node_settings.py — Reset clears the override and goes back to it,
 * rather than writing a frozen copy of today's default into the database.
 */
function NodeInviteEmail() {
    const { subject, body, placeholders, isCustom, loading, save, reset } = useInviteEmailTemplate();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!loading) form.setFieldsValue({ subject, body });
    }, [loading, subject, body, form]);

    const handleSave = values => {
        setBusy(true);
        save(values.subject, values.body)
            .then(() => message.success('Invite wording saved'))
            .catch(err => message.error(err?.message || 'Could not save the invite wording'))
            .finally(() => setBusy(false));
    };

    const handleReset = () => {
        setBusy(true);
        reset()
            .then(() => message.success('Reverted to the default wording'))
            .catch(() => message.error('Could not reset the invite wording'))
            .finally(() => setBusy(false));
    };

    return (
        <Card style={{ marginTop: 16 }}>
            <Space align="center" style={{ marginBottom: 4 }}>
                <Title level={5} style={{ margin: 0 }}>Invite email</Title>
                {!loading && !isCustom && <Tag>Using the default</Tag>}
            </Space>
            <div>
                <Text type="secondary">
                    Sent to someone when a super admin invites them to take over the profile
                    created for them. Editable again per recipient when you send it.
                </Text>
            </div>

            {loading ? (
                <Spin style={{ marginTop: 16 }} />
            ) : (
                <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 16 }}>
                    <Form.Item
                        name="subject"
                        label="Subject"
                        rules={[{ required: true, message: 'Give the email a subject' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="body"
                        label="Message"
                        rules={[{ required: true, message: 'Write a message' }]}
                        extra={
                            <Space size={4} wrap style={{ marginTop: 4 }}>
                                <Text type="secondary">
                                    Replaced when the email is sent:
                                </Text>
                                {placeholders.map(p => (
                                    <Tag key={p} style={{ marginInlineEnd: 0 }}>{`{${p}}`}</Tag>
                                ))}
                            </Space>
                        }
                    >
                        <Input.TextArea rows={8} />
                    </Form.Item>

                    <Space>
                        <Button type="primary" htmlType="submit" loading={busy}>Save</Button>
                        {isCustom && (
                            <Popconfirm
                                title="Revert to the default wording?"
                                description="Your customized subject and message will be discarded."
                                okText="Revert"
                                okButtonProps={{ danger: true }}
                                onConfirm={handleReset}
                            >
                                <Button type="text" danger disabled={busy}>Reset to default</Button>
                            </Popconfirm>
                        )}
                    </Space>
                </Form>
            )}
        </Card>
    );
}

export default NodeInviteEmail;
